import styles from "@styles/Login.module.scss";
import { Text, Button, ButtonGroup } from "@eventsquare/uikit";
import {
  FormLabel,
  FormFieldset,
  Form,
  FormGroup,
} from "@eventsquare/uikit/forms";
import { useState, ChangeEvent, KeyboardEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";

const CODE_LENGTH = 4;

const Verify: React.FC = () => {
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState<string>("");
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  const focusInput = (index: number) => {
    inputsRef.current[index]?.focus();
  };

  const handleChange = (index: number, value: string) => {
    if (/^\d*$/.test(value) && value.length <= 1) {
      const updatedCode = [...code];
      updatedCode[index] = value;
      setCode(updatedCode);
      setError("");

      if (value && index < CODE_LENGTH - 1) {
        focusInput(index + 1);
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (code[index]) {
        const updatedCode = [...code];
        updatedCode[index] = "";
        setCode(updatedCode);
      } else if (index > 0) {
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("Text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (pasted.length === 0) return;

    const updatedCode = [...code];
    pasted.split("").forEach((char, i) => {
      updatedCode[i] = char;
      inputsRef.current[i]?.setSelectionRange(1, 1);
    });

    setCode(updatedCode);
    setError("");
    focusInput(pasted.length - 1);
  };

  const handleSubmit = () => {
    const fullCode = code.join("");

    // Check if code is complete
    if (fullCode.length !== CODE_LENGTH) {
      setError("Please enter all 4 digits");
      return;
    }

    // Verify code
    if (fullCode === "1234") {
      console.log("VERIFIED:", fullCode);
      navigate("/dashboard");
    } else {
      setError("Invalid code. Please try again.");
      setCode(Array(CODE_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.body}>
        <Text variant="h1">Verification code</Text>
        <Text variant="p">
          Please enter the code we just sent to jane.doe@eventsquare.co.
        </Text>
        {error && (
          <Text variant="p" color="error">
            {error}
          </Text>
        )}
        <Form onSubmit={handleSubmit} initialValues={{}}>
          <FormFieldset>
            <FormGroup>
              <FormLabel htmlFor="code">Code</FormLabel>
              <div className={styles.verifyInput}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    ref={(el) => (inputsRef.current[index] = el)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleChange(index, e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                  />
                ))}
              </div>
            </FormGroup>
          </FormFieldset>
          <ButtonGroup>
            <Button type="submit">Confirm</Button>
            <Button variant="link" color="primary" type="button">
              Resend code (60s)
            </Button>
          </ButtonGroup>
        </Form>
      </div>
    </div>
  );
};

export default Verify;
